import { add, getRate, multiplyWithFactor, round, sum, } from "@pentacode/openapi";
import { getSalaryOrDefault } from "./contract.util";
import { Localized } from "./localized";
import { BonusCompType, EmploymentType, timeEntryAbsenceTypes, TimeEntryType, } from "./model";
import { dateAdd, dateSub, formatDate, intersectRanges, toDateString, } from "./utils";
export var PayrollItemType;
(function (PayrollItemType) {
    PayrollItemType["Salary"] = "salary";
    PayrollItemType["TimeWage"] = "time_wage";
    PayrollItemType["Commission"] = "commission";
    PayrollItemType["Absence"] = "absence";
    PayrollItemType["BonusTaxFree"] = "bonus_tax_free";
    PayrollItemType["BonusTaxed"] = "bonus_taxed";
    PayrollItemType["BonusAdvance"] = "bonus_advance";
    PayrollItemType["Benefit"] = "benefit";
    PayrollItemType["WorkDays"] = "work_days";
    PayrollItemType["VacationDays"] = "vacation_days";
    PayrollItemType["SickDays"] = "sick_days";
    PayrollItemType["HourAdjustment"] = "hour_adjustment";
    PayrollItemType["VacationAdjustment"] = "vacation_adjustment";
    PayrollItemType["PayAdvance"] = "pay_advance";
    PayrollItemType["Meals"] = "meals";
})(PayrollItemType || (PayrollItemType = {}));
export function getPayrollReport(company, employee, timeEntries, advances, from, to) {
    const order = [
        PayrollItemType.TimeWage,
        PayrollItemType.Salary,
        PayrollItemType.Absence,
        PayrollItemType.Benefit,
        PayrollItemType.BonusTaxFree,
        PayrollItemType.BonusTaxed,
        PayrollItemType.BonusAdvance,
        PayrollItemType.Meals,
        PayrollItemType.Commission,
        PayrollItemType.HourAdjustment,
        PayrollItemType.VacationAdjustment,
        PayrollItemType.PayAdvance,
        // export only
        PayrollItemType.SickDays,
        PayrollItemType.VacationDays,
        PayrollItemType.WorkDays,
    ];
    const contracts = employee.getAllContractsForRange({ from, to }).filter((c) => !c.blocked);
    if (contracts.length === 0 || !contracts.some((contract) => contract.defaultSalary)) {
        return {
            employee,
            from,
            to,
            items: [],
            totalPay: 0,
        };
    }
    const items = [];
    function addItem(item) {
        const existing = items.find((i) => i.type === item.type &&
            i.wageType === item.wageType &&
            i.costCenter === item.costCenter &&
            i.unit === item.unit &&
            i.wagePerUnit === item.wagePerUnit &&
            i.factor === item.factor &&
            i.description === item.description &&
            i.exportOnly === item.exportOnly &&
            // opposing amounts would cancel each other out while their (absolute) results add up
            i.amount * item.amount >= 0);
        if (existing) {
            existing.amount += item.amount;
            if (item.result) {
                existing.result = (existing.result || 0) + item.result;
            }
        }
        else {
            items.push(item);
        }
    }
    const date = { from, to };
    const defaultPosition = employee.positions[0];
    const defaultCostCenter = company.getCostCenter({ employee, date, position: defaultPosition })?.number;
    const defaultWageTypeSet = company.getWageTypeSet(employee, { employee, date, position: defaultPosition });
    // show prorated payments if multiple payroll periods or multiple contracts
    const startedPayrollPeriods = transformToPayrollIntervals(date, company.settings.startPayrollPeriod).length;
    const extendDescriptionWithPeriod = startedPayrollPeriods > 1 || contracts.length > 1;
    const formatIntervalForDescription = (interval) => {
        return `${formatDate(interval.from)} - ${formatDate(dateAdd(interval.to, { days: -1 }))}`;
    };
    const benefitItems = [];
    // Add payments
    for (const contract of contracts) {
        // monthly salaries
        for (const salary of contract.salaries.filter((s) => s.type === "monthly")) {
            for (const effectiveSalary of getProratedPaymentForRange(contract, date, salary, company.settings.startPayrollPeriod).filter((payment) => payment.amount !== 0)) {
                let description = `Gehalt ${company.getPositionLabel(salary.positionId || defaultPosition)}`;
                if (extendDescriptionWithPeriod) {
                    description += ` (${formatIntervalForDescription(effectiveSalary)})`;
                }
                addItem({
                    type: PayrollItemType.Salary,
                    description,
                    icon: "icon.object.salary",
                    unit: "euros",
                    amount: effectiveSalary.amount,
                    result: effectiveSalary.amount,
                    wageType: company.getWageTypeSet(employee, {
                        employee,
                        position: salary.positionId || defaultPosition,
                        date,
                    })?.salary,
                    costCenter: company.getCostCenter({
                        employee,
                        position: salary.positionId || defaultPosition,
                        date,
                    })?.number,
                });
            }
        }
        // SFN-Advance
        const sfnAdvance = contract.enableSFNLedger && contract.sfnAdvance;
        if (sfnAdvance) {
            for (const sfnAdvancePayment of getProratedPaymentForRange(contract, date, sfnAdvance, company.settings.startPayrollPeriod).filter((payment) => payment.amount !== 0)) {
                let description = "SFN-Pauschale";
                if (extendDescriptionWithPeriod) {
                    description += ` (${formatIntervalForDescription(sfnAdvancePayment)})`;
                }
                addItem({
                    type: PayrollItemType.BonusAdvance,
                    description,
                    icon: "icon.object.bonus",
                    unit: "euros",
                    amount: sfnAdvancePayment.amount,
                    result: sfnAdvancePayment.amount,
                    wageType: defaultWageTypeSet?.bonusAdvance,
                });
            }
        }
        // benefits
        for (const benefit of contract.benefits) {
            const type = company.benefitTypes.find((b) => b.id === benefit.typeId);
            if (!type) {
                continue;
            }
            for (const benefitPayment of getProratedPaymentForRange(contract, date, benefit, company.settings.startPayrollPeriod).filter((payment) => payment.amount !== 0)) {
                let description = type.name;
                if (extendDescriptionWithPeriod) {
                    description += ` (${formatIntervalForDescription(benefitPayment)})`;
                }
                benefitItems.push({
                    amount: benefitPayment.amount,
                    result: benefitPayment.amount,
                    wageType: defaultWageTypeSet?.benefits.find((b) => b.benefitTypeId === benefit.typeId)
                        ?.wageTypeNumber,
                    benefitType: benefit.typeId,
                    from: benefitPayment.from,
                    to: benefitPayment.to,
                    description,
                });
            }
        }
    }
    for (const item of benefitItems.sort((a, b) => `${a.benefitType}${a.from}`.localeCompare(`${b.benefitType}${b.from}`))) {
        addItem({
            type: PayrollItemType.Benefit,
            icon: "icon.object.benefit",
            unit: "euros",
            amount: item.amount,
            result: item.result,
            wageType: item.wageType,
            costCenter: defaultCostCenter,
            description: item.description,
        });
    }
    for (const timeEntry of timeEntries) {
        const result = timeEntry.result;
        if (!result) {
            continue;
        }
        const position = timeEntry.position;
        const contract = employee.getContractForDate(timeEntry.date);
        const salary = getSalaryOrDefault(position?.id, contract?.salaries || []);
        if (!contract || !salary) {
            continue;
        }
        const wageTypeSet = company.getWageTypeSet(employee, { employee, position: position || defaultPosition, date });
        const absenceWageTypes = salary.type === "hourly" ? wageTypeSet?.absenceHourly : wageTypeSet?.absenceMonthly;
        const absenceDaysWageTypes = salary.type === "hourly" ? wageTypeSet?.absenceHourlyDays : wageTypeSet?.absenceMonthlyDays;
        const costCenter = company.getCostCenter({ employee, position: position || defaultPosition, date })?.number;
        const paidDuration = round(add(result.base.duration, result.breaks.paidDuration), 2);
        if (position) {
            const positionLabel = company.getPositionLabel(position) || "Unbekannte Position";
            // Time wage
            if (salary?.type === "hourly") {
                addItem({
                    type: PayrollItemType.TimeWage,
                    description: positionLabel,
                    icon: "icon.object.duration",
                    unit: "hours",
                    amount: paidDuration,
                    wagePerUnit: salary.amount,
                    result: salary.amount * paidDuration,
                    wageType: wageTypeSet?.workHourly,
                    costCenter,
                });
            }
            else {
                addItem({
                    type: PayrollItemType.TimeWage,
                    description: positionLabel,
                    icon: "icon.object.duration",
                    unit: "hours",
                    amount: paidDuration,
                    wageType: wageTypeSet?.workMonthly,
                    costCenter,
                });
            }
            // Work days
            if (result.days) {
                addItem({
                    type: PayrollItemType.WorkDays,
                    description: `${positionLabel} (Tage)`,
                    icon: "icon.object.day-view",
                    unit: "days",
                    amount: result.days,
                    wageType: salary.type === "hourly" ? wageTypeSet?.workHourlyDays : wageTypeSet?.workMonthlyDays,
                    costCenter,
                    exportOnly: true,
                });
            }
            // commission
            if (salary.commission && timeEntry.revenue) {
                addItem({
                    type: PayrollItemType.Commission,
                    description: positionLabel,
                    icon: "icon.object.bonus-taxable",
                    unit: "euros",
                    factor: salary.commission,
                    amount: timeEntry.revenue,
                    result: (timeEntry.revenue * salary.commission) / 100,
                    wageType: wageTypeSet?.commission,
                    costCenter,
                });
            }
            // meals
            if (result.meals.breakfast && company.settings.mealValueBreakfast) {
                addItem({
                    type: PayrollItemType.Meals,
                    description: `Frühstück (${positionLabel})`,
                    icon: "icon.object.meal",
                    unit: "pieces",
                    wagePerUnit: result.meals.breakfast.value,
                    amount: result.meals.breakfast.count,
                    result: result.meals.breakfast.costs,
                    wageType: wageTypeSet?.mealsBreakfast,
                    costCenter,
                });
            }
            if (result.meals.lunch && company.settings.mealValueLunch) {
                addItem({
                    type: PayrollItemType.Meals,
                    description: `Mittagessen (${positionLabel})`,
                    icon: "icon.object.meal",
                    unit: "pieces",
                    wagePerUnit: result.meals.lunch.value,
                    amount: result.meals.lunch.count,
                    result: result.meals.lunch.costs,
                    wageType: wageTypeSet?.mealsLunch,
                    costCenter,
                });
            }
            if (result.meals.dinner && company.settings.mealValueDinner) {
                addItem({
                    type: PayrollItemType.Meals,
                    description: `Abendessen (${positionLabel})`,
                    icon: "icon.object.meal",
                    unit: "pieces",
                    wagePerUnit: result.meals.dinner.value,
                    amount: result.meals.dinner.count,
                    result: result.meals.dinner.costs,
                    wageType: wageTypeSet?.mealsDinner,
                    costCenter,
                });
            }
            const totalMealsValue = sum(result.meals.breakfast?.costs || 0, result.meals.lunch?.costs || 0, result.meals.dinner?.costs || 0);
            if (totalMealsValue > 0) {
                addItem({
                    type: PayrollItemType.Benefit, // PayrollItemType.Meals is only for the number of meals, PayrollItemType.Benefit is used for the euro value here
                    description: `Mitarbeiteressen (${positionLabel})`,
                    icon: "icon.object.meal",
                    unit: "euros",
                    amount: totalMealsValue,
                    result: totalMealsValue,
                    wageType: wageTypeSet?.mealsValue,
                    costCenter,
                    exportOnly: true,
                });
            }
        }
        else if (timeEntryAbsenceTypes.some((absenceType) => absenceType === timeEntry.type)) {
            const type = timeEntry.type;
            // Absence hours
            addItem({
                type: PayrollItemType.Absence,
                description: Localized[company.country].timeEntryTypeLabel(timeEntry),
                icon: Localized[company.country].timeEntryTypeIcon(type),
                unit: "hours",
                amount: round(result.base.duration, 2),
                wagePerUnit: salary?.type === "hourly" ? result.base.hourlyRate : undefined,
                result: salary?.type === "hourly" ? result.base.wages : undefined,
                wageType: {
                    [TimeEntryType.Vacation]: absenceWageTypes?.vacation,
                    [TimeEntryType.Sick]: absenceWageTypes?.sick,
                    [TimeEntryType.ChildSick]: absenceWageTypes?.childSick,
                    [TimeEntryType.SickInKUG]: absenceWageTypes?.sickInKUG,
                    [TimeEntryType.CompDay]: absenceWageTypes?.compDay,
                }[type],
                costCenter,
            });
            // absence days
            if (result.days) {
                addItem({
                    type: type === TimeEntryType.Vacation ? PayrollItemType.VacationDays : PayrollItemType.SickDays,
                    description: `${Localized[company.country].timeEntryTypeLabel(timeEntry)} (Tage)`,
                    icon: Localized[company.country].timeEntryTypeIcon(type),
                    unit: "days",
                    amount: result.days,
                    wageType: {
                        [TimeEntryType.Vacation]: absenceDaysWageTypes?.vacation,
                        [TimeEntryType.Sick]: absenceDaysWageTypes?.sick,
                        [TimeEntryType.ChildSick]: absenceDaysWageTypes?.childSick,
                        [TimeEntryType.SickInKUG]: absenceDaysWageTypes?.sickInKUG,
                        [TimeEntryType.CompDay]: absenceDaysWageTypes?.compDay,
                    }[type],
                    costCenter,
                    exportOnly: true,
                });
            }
        }
        else if (timeEntry.type === TimeEntryType.HourAdjustment && result.base.duration) {
            if (!timeEntry.paid) {
                continue;
            }
            // Add Hour Adjustments
            addItem({
                type: PayrollItemType.HourAdjustment,
                description: timeEntry.comment || Localized[company.country].timeEntryTypeLabel(timeEntry),
                icon: "icon.time-entry.adjustment",
                unit: "hours",
                wagePerUnit: result.base.hourlyRate,
                amount: round(result.base.duration, 2),
                result: Math.abs(result.base.wages),
                wageType: timeEntry.wageType || undefined,
                costCenter,
            });
        }
        else if (timeEntry.type === TimeEntryType.VacationAdjustment && result.base.duration) {
            // Add Vacation Adjustments
            addItem({
                type: PayrollItemType.VacationAdjustment,
                description: `${timeEntry.comment || Localized[company.country].timeEntryTypeLabel(timeEntry)} (${result.days} T)`,
                icon: "icon.time-entry.adjustment",
                unit: "hours",
                wagePerUnit: result.base.hourlyRate,
                amount: round(result.base.duration, 2),
                result: result.base.wages,
                wageType: timeEntry.wageType || undefined,
                costCenter,
            });
        }
        // Add bonuses
        for (const bonus of result.bonuses) {
            const type = company.bonusTypes.find((t) => t.id === bonus.type.id);
            if (!type || !bonus.duration) {
                continue;
            }
            const wageTypes = wageTypeSet?.bonuses.find((b) => b.bonusTypeId === bonus.type.id);
            const activeContract = contracts.find((c) => c.isActiveOn(timeEntry.date));
            addItem({
                // TODO: this is already set to false on sick and vacation days, should we even decide anything here?
                type: bonus.taxFree ? PayrollItemType.BonusTaxFree : PayrollItemType.BonusTaxed,
                description: getBonusDescription(type.name, timeEntry.type),
                icon: "icon.object.bonus",
                unit: type.compType === BonusCompType.FixedAmount ? "days" : "hours",
                amount: type.compType === BonusCompType.FixedAmount ? 1 : round(bonus.duration, 2),
                wagePerUnit: type.compType === BonusCompType.FixedAmount ? bonus.wages : bonus.hourlyRate,
                factor: bonus.percent,
                result: activeContract?.enableSFNLedger && activeContract.sfnAdvance && bonus.taxFree
                    ? undefined
                    : bonus.wages,
                wageType: getBonusWageType(timeEntry.type, wageTypes, bonus.taxFree),
                costCenter,
            });
        }
    }
    // Add Pay Advance
    for (const entry of advances) {
        addItem({
            type: PayrollItemType.PayAdvance,
            description: `Vorschuss vom ${formatDate(entry.date)} (${entry.cashbook ? "bar" : "Überweisung"})`,
            icon: "icon.object.pay-advance",
            unit: "euros",
            amount: entry.amount,
            result: entry.amount,
            wageType: defaultWageTypeSet?.payAdvance,
            costCenter: defaultCostCenter,
        });
    }
    return {
        employee,
        from,
        to,
        items: items.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type)),
        totalPay: items.filter((item) => !item.exportOnly).reduce((total, item) => total + (item.result || 0), 0),
    };
}
export function getBonusDescription(name, timeEntryType) {
    // Add a suffix on sick days
    if ([TimeEntryType.Sick, TimeEntryType.ChildSick, TimeEntryType.SickInKUG].includes(timeEntryType)) {
        return `${name} (Krank)`;
    }
    // Add a suffix on vacation days
    if ([TimeEntryType.Vacation].includes(timeEntryType)) {
        return `${name} (Urlaub)`;
    }
    return name;
}
/**
 * Get the wage type for a bonus based on the time entry type and bonus salary configuration.
 * Specifically overrides tax-free bonuses for sick and vacation days.
 *
 * Bonus results are already split by tax-free and taxed parts for work entries, or are fully taxed for absence entries.
 * However we need to additionally distinguish the wage type for sick and vacation days.
 */
export function getBonusWageType(timeEntryType, bonusSalaryConfig, isTaxFree) {
    if (!bonusSalaryConfig) {
        return undefined;
    }
    // Bonus on sick days are always taxed
    if ([TimeEntryType.Sick, TimeEntryType.ChildSick, TimeEntryType.SickInKUG].includes(timeEntryType)) {
        return bonusSalaryConfig.taxedSick || bonusSalaryConfig.taxed; // Fallback to taxed if taxedSick is not set
    }
    // Bonus on vacation days are always taxed
    if ([TimeEntryType.Vacation].includes(timeEntryType)) {
        return bonusSalaryConfig.taxedVacation || bonusSalaryConfig.taxed; // Fallback to taxed if taxedVacation is not set
    }
    // For all other time entry types, return the appropriate wage type based on tax status
    return isTaxFree ? bonusSalaryConfig.taxFree : bonusSalaryConfig.taxed;
}
/**
 * Get hourly rate in euro cents for a given contract and position
 */
export function getHourlyRate(settings, companyBenefitTypes, contract, positionId) {
    const weekFactor = settings.weekFactor || 4.35;
    const comp = getSalaryOrDefault(positionId, contract.salaries);
    if (!comp) {
        return 0;
    }
    if (comp.type === "hourly") {
        return getRate(comp.amount, 1);
    }
    const salaryTotal = sum(comp.amount, ...contract.benefits
        .filter((b) => companyBenefitTypes?.find((t) => t.id === b.typeId)?.includeInBonusPayments)
        .map((benefit) => benefit.amount));
    const averageHoursPerMonth = multiplyWithFactor(contract.nominalWeeklyHours, weekFactor);
    return round(getRate(salaryTotal, averageHoursPerMonth), 2);
}
export function getAncillaryCostFactor(type) {
    const ANCILLARY_COST_FACTORS = {
        [EmploymentType.ShortTerm]: [0, 0],
        [EmploymentType.Independent]: [0, 0],
        [EmploymentType.Marginal]: [0.348, 0],
        [EmploymentType.Regular]: [0.19825, 0.19825],
        [EmploymentType.Trainee]: [0.19825, 0.19825],
        [EmploymentType.MidiJob]: [0.19825, 0.19825],
        [EmploymentType.WorkingStudent]: [0.0935, 0.0935],
        [EmploymentType.DualStudent]: [0.19825, 0.19825],
        [EmploymentType.Intern]: [0, 0],
    };
    return ANCILLARY_COST_FACTORS[type] || [0, 0];
}
/*
    Definition: A Payment is either:
    - a Salary
    - a Benefit
    - a SFN Advance

    A Contract is basically a DateRange as is the range we are looking at.
    Since payment calculations are always done on a monthly payroll period basis, we need to split the supplied range into month-long payroll periods (intervals).
    For each interval, we calculate the number of days the contract is active and then calculate the prorated payment for that period.
    To calculate the prorated payment correctly we need to consider the following cases:

    1. contract is active whole period --> entire payment
    |----- period -----|
    |------ con. -----|

    2. contract begins and ends within period --> payment for days with contract
    |----- period -----|
       |-- con. --|

    3. contract begins in period --> payment for days with contract until end of period
    |----- period -----|
           |-- con. ---------|

    4. contract ends in period --> payment for days with contract starting at beginning of period
          |----- period -----|
    |------- con. ---|

*/
export function getProratedPaymentForRange(contractRange, range, payment, startDayOfPayrollPeriod = 1) {
    const baseValue = typeof payment === "number" ? payment : payment.amount;
    const effectiveRange = intersectRanges(range, {
        from: contractRange.start,
        to: contractRange.end ?? toDateString(new Date(Number.MAX_SAFE_INTEGER)),
    });
    if (!effectiveRange) {
        console.log("No effective range - returning empty array");
        return [];
    }
    const payrollPeriods = transformToPayrollIntervals(effectiveRange, startDayOfPayrollPeriod);
    return payrollPeriods.map((period) => {
        const fullPayrollPeriod = getPayrollPeriod(period.from, startDayOfPayrollPeriod);
        const result = calculateProratedPayment(period, fullPayrollPeriod, baseValue);
        return result;
    });
}
export function calculateProratedPayment(activeRange, baseRange, baseValue) {
    const effectiveRange = intersectRanges(baseRange, activeRange);
    if (!effectiveRange) {
        console.log("Invalid date range - earliest end is before latest start!!!");
        return {
            from: baseRange.from,
            to: baseRange.to,
            amount: 0,
        };
    }
    // Calculate the number of days in the month and the number of days the contract is active
    const totalDaysInRange = dateSub(baseRange.from, baseRange.to);
    const activeDays = dateSub(effectiveRange.from, effectiveRange.to);
    // Calculate the prorated payment
    const proratedPayment = round((activeDays / totalDaysInRange) * baseValue, 2);
    return {
        from: effectiveRange.from,
        to: effectiveRange.to,
        amount: proratedPayment,
    };
}
export function transformToPayrollIntervals(dateRange, startDayOfPayrollInterval = 1) {
    const { from, to } = dateRange;
    const intervals = [];
    let currentStart = from;
    while (currentStart < to) {
        // Get first day of next payroll periods
        const endOfPayrollPeriod = getPayrollPeriod(currentStart, startDayOfPayrollInterval).to;
        // Determine the end date of the current interval
        const currentEndDate = endOfPayrollPeriod < to ? endOfPayrollPeriod : to;
        // Push the interval into the array
        intervals.push({
            from: currentStart,
            to: currentEndDate,
        });
        // Move to the start of the next payment period
        currentStart = currentEndDate;
    }
    return intervals;
}
/** finds the payroll period that contains the given date */
export function getPayrollPeriod(date, startOfPayrollPeriod) {
    const [year, month] = date.split("-");
    let periodStart = `${year}-${month}-${startOfPayrollPeriod.toString().padStart(2, "0")}`;
    // If the date is before the start day of the period, we need to use the previous month
    if (date < periodStart) {
        periodStart = dateAdd(periodStart, { months: -1 });
    }
    const periodEnd = dateAdd(periodStart, { months: 1 });
    return {
        from: periodStart,
        to: periodEnd,
    };
}
//# sourceMappingURL=payroll.js.map